import { useFormikContext } from 'formik';
import { useMemo } from 'react';

import MyTextInput from '../../../../components/MyTextInput';
import MyRadio, { MyFieldError } from '../../../../components/MyRadio';
import { StatusOptions } from '../models/json';

type Props = {
	disabled: boolean;
	statusOptions: StatusOptions[];
};

const statusOptionOrder = [1, 2, 3, 5, 4];

const BmwMainForm = (props: Props) => {
	const { handleSubmit, values, isSubmitting, isValid, dirty } =
		useFormikContext<any>();
	const statusKey = Number(values.statusKey);
	const sortedStatusOptions = useMemo(
		() =>
			[...props.statusOptions].sort((a, b) => {
				const aIndex = statusOptionOrder.indexOf(a.Key);
				const bIndex = statusOptionOrder.indexOf(b.Key);

				if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
				if (aIndex !== -1) return -1;
				if (bIndex !== -1) return 1;

				return a.Key - b.Key;
			}),
		[props.statusOptions],
	);
	const selectedStatusOption = sortedStatusOptions.find(
		(op) => op.Key === statusKey,
	);
	const isNewOwnerStatus = statusKey === 1;
	const isComplaintStatus = statusKey === 2;
	const isOtherStatus = statusKey === 4;
	const isEmailVisible = statusKey !== 5;
	const isStatusWithoutDetails =
		!!selectedStatusOption &&
		!isNewOwnerStatus &&
		!isComplaintStatus &&
		!isOtherStatus;

	return (
		<form className='bmw-main-form' onSubmit={handleSubmit}>
			<section className='bmw-form-card bmw-form-card--status'>
				<div className='row'>
					<h4>STATO VEICOLO</h4>

					<div className='col-md-3 mt-3'>Il Veicolo è stato</div>

					<div className='col-md-9 mt-3'>
						<div className='app-radio-group'>
							{sortedStatusOptions.map((op) => (
								<MyRadio
									key={op.Key}
									name='statusKey'
									value={op.Key}
									label={op.Value.toLowerCase()}
									disabled={props.disabled}
								/>
							))}
						</div>

						<MyFieldError name='statusKey' />
					</div>
				</div>
			</section>

			<section className='bmw-form-card bmw-form-card--details'>
				{isEmailVisible && (
					<div className='row mt-0'>
						<h6>CONTATTO</h6>

						<div className='col-md-6 mt-3'>
							<MyTextInput
								disabled={props.disabled}
								type='email'
								className='form-control'
								placeholder='Email'
								name='email'
							/>
						</div>
					</div>
				)}

				{isNewOwnerStatus && (
					<>
						<h6 className='text-start mt-4'>
							DA COMPILARE IN CASO DI VENDITA VEICOLO NUOVO INTESTATARIO
						</h6>

						<div className='row'>
							<div className='mt-3 col-md-6'>
								<MyTextInput
									disabled={props.disabled}
									type='text'
									className='form-control'
									placeholder='Cognome'
									name='newOwnerSurname'
								/>
							</div>

							<div className='mt-3 col-md-6'>
								<MyTextInput
									disabled={props.disabled}
									type='text'
									className='form-control'
									placeholder='Nome'
									name='newOwnerName'
								/>
							</div>
						</div>

						<div className='row'>
							<div className='mt-3 col-md-4'>
								<MyTextInput
									disabled={props.disabled}
									type='text'
									className='form-control'
									placeholder='Indirizzo'
									name='newOwnerAddress'
								/>
							</div>

							<div className='mt-3 col-md-4'>
								<MyTextInput
									disabled={props.disabled}
									type='text'
									className='form-control'
									placeholder='N°'
									name='newOwnerStreetNumber'
								/>
							</div>

							<div className='mt-3 col-md-4'>
								<MyTextInput
									disabled={props.disabled}
									type='text'
									className='form-control'
									placeholder='CAP'
									name='newOwnerCap'
								/>
							</div>

							<div className='mt-3 col-md-4'>
								<MyTextInput
									disabled={props.disabled}
									type='text'
									className='form-control'
									placeholder='Località'
									name='newOwnerCity'
								/>
							</div>

							<div className='mt-3 col-md-4'>
								<MyTextInput
									disabled={props.disabled}
									type='text'
									className='form-control'
									placeholder='Provincia'
									name='newOwnerProvince'
								/>
							</div>

							<div className='mt-3 col-md-4'>
								<MyTextInput
									disabled={props.disabled}
									type='text'
									className='form-control'
									placeholder='Stato'
									name='newOwnerState'
								/>
							</div>
						</div>
					</>
				)}

				{isComplaintStatus && (
					<>
						<h6 className='text-start mt-4'>
							DA COMPILARE IN CASO DI FURTO / DENUNCIA PERDITA POSSESSO
						</h6>

						<div className='row'>
							<div className='mt-3 col-md-6'>
								<MyTextInput
									disabled={props.disabled}
									type='date'
									className='form-control'
									placeholder='Data denuncia'
									name='complaintDate'
								/>
							</div>
						</div>
					</>
				)}

				{isStatusWithoutDetails && (
					<div className='row mt-4'>
						<div className='col-12'>
							<h6 className='text-start'>
								{isEmailVisible
									? 'NESSUN DATO AGGIUNTIVO RICHIESTO (OLTRE ALL\'EMAIL).'
									: 'NESSUN DATO AGGIUNTIVO RICHIESTO.'}
							</h6>
						</div>
					</div>
				)}

				{isOtherStatus && (
					<>
						<h6 className='text-start mt-4'>
							DA COMPILARE IN CASO DI ALTRO
						</h6>

						<div className='row'>
							<div className='mt-3 col-12'>
								<MyTextInput
									disabled={props.disabled}
									type='text'
									className='form-control'
									placeholder='Specifica cosa è successo'
									name='other'
								/>
							</div>
						</div>
					</>
				)}

				<div className='col-md-2 d-flex mt-4'>
					<button
						className='bmw-btn-submit btn btn-primary flex-grow-1'
						type='submit'
						disabled={isSubmitting || !isValid || !dirty || props.disabled}
					>
						Invia dati
					</button>
				</div>

				<div className='bmw-privacy-note mt-4'>
					Cliccando su &quot;invia dati&quot; i dati personali relativi al nuovo
					proprietario saranno comunicati a BMW Italia S.p.A. che li tratterà nel
					rispetto di quanto previsto dal GDPR (Regolamento UE 2016/679)
					esclusivamente al fine di trasmettere tempestivamente la lettera di
					&quot;Azione di richiamo&quot;.
				</div>
			</section>
		</form>
	);
};

export default BmwMainForm;
